import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Link from 'next/link'

import styles from './AddComment.module.scss'

interface AddCommentProps {
  addCommentHandler: any
  isUserLoggedIn: boolean
  parentID?: any
  user: any
}

const AddComment = ({
  addCommentHandler,
  user,
  isUserLoggedIn,
  parentID,
}: AddCommentProps) => {
  const [comment, setComment] = useState('')
  const [placeholderDiv, setPlaceholderDiv] = useState(null)
  const [showInfoInputs, setShowInfoInputs] = useState(false)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [hasAgreedToRules, setHasAgreedToRules] = useState(false)
  const [usernameColorClass, setUsernameColorClass] = useState(styles.goldText)
  const [emailColorClass, setEmailColorClass] = useState(styles.goldText)
  const [rulesColorClass, setRulesColorClass] = useState('')
  const [errorMsg, setErrorMsg] = useState(null)

  const inputRef = useRef(null)

  const placeholderDivJSX = (
    <div
      className={styles.placeholder}
      unselectable="on"
      onClick={() => {
        setPlaceholderDiv(null)
        inputRef.current?.focus()
        setShowInfoInputs(true)
      }}
    >
      Ostavi komentar...
    </div>
  )

  // This sets the cursor to the end of a contentEditable div
  const setEndOfContenteditable = (contentEditableElement) => {
    let range, selection
    if (document.createRange) {
      range = document.createRange()
      range.selectNodeContents(contentEditableElement)
      range.collapse(false)
      selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }

  // Note that this textarea is uncontrolled because it is a contentEditable div instead
  // of a textarea, so that's why it's not enough to use setComment() to prevent more than
  // 500 characters
  const onInputCommentHandler = (e) => {
    if (!showInfoInputs) {
      setShowInfoInputs(true)
    }
    if (e.target.innerText.length <= 500) {
      setComment(e.target.innerText)
    } else {
      e.target.innerText = comment
      setEndOfContenteditable(e.target)
      // eslint-disable-next-line no-console
      console.log('over 500 chars')
    }
  }

  const sendCommentClickHandler = async () => {
    let authorUsername = ''
    let authorEmail = ''
    if (isUserLoggedIn) {
      authorUsername = user.name
      authorEmail = user.email
    } else if (!isUserLoggedIn) {
      authorUsername = username
      authorEmail = email
    }
    if (authorUsername !== '' && authorEmail !== '' && hasAgreedToRules) {
      try {
        await addCommentHandler(
          comment,
          parentID ? parentID : null,
          authorUsername,
          authorEmail
        )
      } catch (e) {
        if (e.response.status === 401) {
          return setErrorMsg('Već postoji korisnik sa tom e-mail adresom')
        }
      }
      setErrorMsg('')
      setComment('')
      inputRef.current ? (inputRef.current.innerText = '') : null
    } else {
      if (authorUsername === '') {
        setUsernameColorClass(styles.redText)
      }
      if (authorEmail === '') {
        setEmailColorClass(styles.redText)
      }
      if (!hasAgreedToRules) {
        setRulesColorClass(styles.redRulesText)
      }
    }
  }

  useEffect(() => {
    if (comment === '') {
      setPlaceholderDiv(placeholderDivJSX)
    } else if (comment !== '') {
      setPlaceholderDiv(null)
    }
  }, [comment])

  return (
    <div className={`${styles.addComment} mt-8`}>
      <div
        className={`${styles.commentContainer} flex border-b border-grayText`}
      >
        <div
          ref={inputRef}
          className={`${styles.comment} text-gold border-0 outline-none w-full`}
          contentEditable
          role="textbox"
          onInput={onInputCommentHandler}
          aria-multiline
          aria-autocomplete="list"
          aria-label="Send a message"
          spellCheck="false"
        />
        <div
          className={`${styles.charNum} ${
            comment.length < 120 ? styles.hideCharNum : styles.showCharNum
          }`}
        >
          {500 - comment.length}
        </div>

        {placeholderDiv}
        <div className={styles.commentRight}>
          <svg
            height="20"
            className={`${styles.send} w-8 cursor-pointer`}
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
            onClick={sendCommentClickHandler}
          >
            <path d="M4.02 42l41.98-18-41.98-18-.02 14 30 4-30 4z" />
            <path d="M0 0h48v48h-48z" fill="none" />
          </svg>
        </div>
      </div>

      <>
        {showInfoInputs && !isUserLoggedIn ? (
          <div className={styles.userInfo}>
            <textarea
              className={`${styles.infoInput} ${usernameColorClass} text-gold border-0 border-b border-grayText outline-none w-full flex-1 resize-none overflow-hidden`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onFocus={() => setUsernameColorClass(styles.goldText)}
              placeholder="Vaše Ime"
              maxLength={25}
              spellCheck="false"
            />
            <textarea
              className={`${styles.infoInput} ${emailColorClass} text-gold border-0 border-b border-grayText outline-none w-full flex-1 resize-none overflow-hidden`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailColorClass(styles.goldText)}
              placeholder="Email"
              maxLength={50}
              spellCheck="false"
            />
          </div>
        ) : null}
        {showInfoInputs ? (
          <>
            <input
              className={styles.rulesCheckbox}
              style={{
                marginTop: isUserLoggedIn ? 20 : 0,
              }}
              type="checkbox"
              checked={hasAgreedToRules}
              onChange={() => {
                setHasAgreedToRules(!hasAgreedToRules)
                setRulesColorClass('')
              }}
              name="rulesCheckbox"
            />
            <Link href="/about/comment-policy">
              <a target="_blank" rel="noopener noreferrer">
                <label
                  className={`${styles.rulesCheckboxLabel} ${rulesColorClass}`}
                  style={{
                    marginTop: isUserLoggedIn ? 15 : 0,
                  }}
                  htmlFor="rulesCheckbox"
                  unselectable="on"
                >
                  Prihvatam pravila komentarisanja
                </label>
              </a>
            </Link>
          </>
        ) : null}
      </>

      {errorMsg ? <div className={styles.errorMsg}>{errorMsg}</div> : null}
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    isUserLoggedIn: state.isUserLoggedIn,
    user: state.user,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    displaySignupForm: () =>
      dispatch({
        type: 'SHOW_SIGNUP_MODAL',
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AddComment)
